import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogTrigger } from './ui/Dialog';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import { apiFetch } from '../lib/api';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import {
  STEP_UP_ENDED_MESSAGE,
  isStepUpRequired,
  stepUpBody,
  stepUpCodeReady,
  useStepUpWindow,
} from '../lib/stepUp';
import { StepUpCodeField } from './StepUpCodeField';
import { ApiError } from '../types/api';

function resumeErrorText(err: unknown): string {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (isStepUpRequired(err)) return STEP_UP_ENDED_MESSAGE;
  if (err instanceof ApiError && err.status === 401)
    return 'The code is incorrect or already used.';
  if (err instanceof ApiError && err.status === 403) return 'Only an administrator can do this.';
  return 'Sending could not be resumed. Try again.';
}

/** Turns the kill switch off again. Needs a fresh 2FA code (step-up). */
export function ResumeSendingDialog({ compact = false }: { compact?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const queryClient = useQueryClient();
  const stepUp = useStepUpWindow();

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch('/kill-switch/disengage', {
        method: 'POST',
        body: stepUp.active ? {} : stepUpBody(totpCode),
      }),
    onSuccess: () => {
      stepUp.onStepUpSuccess();
      setOpen(false);
      setTotpCode('');
      void queryClient.invalidateQueries({ queryKey: ['health-summary'] });
    },
    onError: stepUp.onStepUpError,
  });

  const canConfirm = (stepUp.active || stepUpCodeReady(totpCode)) && !mutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setTotpCode('');
          mutation.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="primary"
          className={compact ? 'h-8 px-3 text-[13px]' : 'w-full h-12 font-semibold'}
        >
          Resume sending
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Resume sending"
        description="Turns the kill switch off. Campaigns that were paused stay paused until you resume them."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canConfirm) mutation.mutate();
          }}
        >
          {mutation.isError ? <Alert>{resumeErrorText(mutation.error)}</Alert> : null}
          <StepUpCodeField
            id="resume-code"
            value={totpCode}
            onChange={setTotpCode}
            active={stepUp.active}
            minutesLeft={stepUp.minutesLeft}
          />
          <DialogFooter>
            <DialogClose>
              <Button type="button" variant="secondary" className="h-11">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" variant="primary" disabled={!canConfirm} className="h-11">
              {mutation.isPending ? 'Resuming…' : 'Resume sending'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
